import AssetDownload from '@comp/product/asset-download'
import CompliancePage, {
  CompliancePageContent,
} from '@comp/compliance/compliance-template'

const content: CompliancePageContent = {
  path: '/compliance/kvkk',
  bilingual: true,
  seoTitle: {
    en: 'KVKK Compliance: Identity & Access Controls | Monofor',
    tr: 'KVKK Uyumu: Kimlik ve Erişim Kontrolleri | Monofor',
  },
  seoDescription: {
    en: 'Meet KVKK technical measures with Monofor: MFA, access control, privileged session recording, and audit-ready KVKK report templates. Self-hosted in Türkiye if you need it.',
    tr: 'KVKK teknik tedbirlerini Monofor ile karşılayın: MFA, erişim kontrolü, ayrıcalıklı oturum kaydı ve denetime hazır KVKK rapor şablonları. İsterseniz Türkiye’de kendi altyapınızda.',
  },
  heroEyebrow: { en: 'KVKK compliance', tr: 'KVKK uyumu' },
  heroTitle: {
    en: 'KVKK technical measures, handled by your identity layer.',
    tr: 'KVKK teknik tedbirleri, kimlik katmanınızda hazır.',
  },
  heroBody: {
    en: 'Law No. 6698 expects controllers to prevent unlawful access to personal data. Monofor turns that expectation into enforced controls: MFA on every login, least-privilege access, recorded admin sessions, and evidence your auditors can export.',
    tr: '6698 sayılı Kanun, veri sorumlularından kişisel veriye hukuka aykırı erişimi önlemesini bekler. Monofor bu beklentiyi uygulanan kontrollere çevirir: her girişte MFA, en az ayrıcalıklı erişim, kayıt altındaki yönetici oturumları ve denetçilerinize dışa aktarabileceğiniz kanıtlar.',
  },
  contextHeading: {
    en: 'What KVKK expects from access management',
    tr: 'KVKK erişim yönetiminden ne bekliyor',
  },
  contextBody: {
    en: 'KVKK (Law No. 6698 on the Protection of Personal Data) obliges data controllers to take the technical and organizational measures needed to prevent unlawful processing of and access to personal data, and to preserve it. The Personal Data Protection Board’s published guidance on technical measures repeatedly lands on identity: user authorization and authentication, access logs, privileged account discipline, and the ability to prove who touched which system when.\n\nIn practice this means the systems that hold personal data (HR software, CRMs, databases, file servers) need centrally managed identities, strong authentication, time-bound admin access, and tamper-evident audit trails. That is an identity platform’s job, and it is exactly the layer Monofor provides.',
    tr: 'KVKK (6698 sayılı Kişisel Verilerin Korunması Kanunu), veri sorumlularını kişisel verilerin hukuka aykırı işlenmesini ve bu verilere hukuka aykırı erişimi önlemek, verilerin muhafazasını sağlamak için gerekli teknik ve idari tedbirleri almakla yükümlü kılar. Kişisel Verileri Koruma Kurulu’nun yayımladığı teknik tedbir rehberleri tekrar tekrar kimlik konusuna gelir: kullanıcı yetkilendirme ve kimlik doğrulama, erişim logları, ayrıcalıklı hesap disiplini ve hangi sisteme kimin ne zaman dokunduğunu kanıtlayabilme.\n\nPratikte bu, kişisel veri barındıran sistemlerin (İK yazılımları, CRM’ler, veritabanları, dosya sunucuları) merkezî yönetilen kimliklere, güçlü kimlik doğrulamaya, süre sınırlı yönetici erişimine ve kurcalamaya karşı korumalı denetim izlerine ihtiyaç duyması demektir. Bu bir kimlik platformunun işidir ve Monofor tam olarak bu katmanı sağlar.',
  },
  requirementsHeading: {
    en: 'Requirement to control, mapped.',
    tr: 'Gereklilikten kontrole, eşlenmiş.',
  },
  requirementsBody: {
    en: 'The recurring identity-related measures from KVKK guidance, and the Monofor capability that enforces each one.',
    tr: 'KVKK rehberlerinde tekrar eden kimlik odaklı tedbirler ve her birini uygulayan Monofor yeteneği.',
  },
  requirements: [
    {
      requirement: {
        en: 'User authorization & authentication',
        tr: 'Kullanıcı yetkilendirme ve kimlik doğrulama',
      },
      detail: {
        en: 'Access to systems holding personal data must be authenticated and role-appropriate.',
        tr: 'Kişisel veri barındıran sistemlere erişim kimlik doğrulamalı ve role uygun olmalıdır.',
      },
      capability: {
        en: 'Monosign centralizes SSO across 7,000+ apps with MFA on every login, from TOTP to phishing-resistant passkeys, and adaptive step-up when risk signals change.',
        tr: 'Monosign 7.000’den fazla uygulamada SSO’yu merkezileştirir; her girişte TOTP’den kimlik avına dirençli geçiş anahtarlarına kadar MFA ve risk sinyali değiştiğinde uyarlanabilir ek doğrulama uygular.',
      },
      productHref: '/sign',
      productName: 'Monosign',
    },
    {
      requirement: {
        en: 'Least privilege & authorization control',
        tr: 'En az ayrıcalık ve yetki kontrolü',
      },
      detail: {
        en: 'Users should hold only the access their role requires; authorization matrices must stay current.',
        tr: 'Kullanıcılar yalnızca rollerinin gerektirdiği erişime sahip olmalı; yetki matrisleri güncel tutulmalıdır.',
      },
      capability: {
        en: 'Lifecycle workflows grant and revoke access by role, joiner-mover-leaver automation removes stale access the day someone changes jobs, and access reviews keep the matrix provably current.',
        tr: 'Yaşam döngüsü akışları erişimi role göre verip geri alır; işe giriş-geçiş-ayrılış otomasyonu görev değişiminde bayat erişimi aynı gün kaldırır; erişim gözden geçirmeleri yetki matrisini kanıtlanabilir şekilde güncel tutar.',
      },
      productHref: '/sync',
      productName: 'Monosync',
    },
    {
      requirement: {
        en: 'Privileged account discipline',
        tr: 'Ayrıcalıklı hesap disiplini',
      },
      detail: {
        en: 'Admin access to personal-data systems is the highest-risk path and needs its own controls.',
        tr: 'Kişisel veri sistemlerine yönetici erişimi en yüksek riskli yoldur ve kendi kontrollerine ihtiyaç duyar.',
      },
      capability: {
        en: 'Monopam vaults privileged credentials, brokers admin sessions in the browser, records them as video plus keystroke transcript, and limits elevation to approved, time-bound windows.',
        tr: 'Monopam ayrıcalıklı kimlik bilgilerini kasada tutar, yönetici oturumlarını tarayıcı üzerinden aracılar, video ve tuş kaydıyla kaydeder, yükseltmeyi onaylı ve süre sınırlı pencerelerle kısıtlar.',
      },
      productHref: '/pam',
      productName: 'Monopam',
    },
    {
      requirement: {
        en: 'Access logging & traceability',
        tr: 'Erişim kaydı ve izlenebilirlik',
      },
      detail: {
        en: 'Who accessed which system, when, and what changed must be answerable, with logs that hold up.',
        tr: 'Hangi sisteme kimin, ne zaman eriştiği ve neyin değiştiği, geçerliliği olan loglarla yanıtlanabilir olmalıdır.',
      },
      capability: {
        en: 'Every authentication, elevation, and entitlement change is logged tamper-evident across the platform, exportable to your SIEM, with pre-built KVKK report templates for audits.',
        tr: 'Her kimlik doğrulama, yetki yükseltme ve yetkilendirme değişikliği platform genelinde kurcalamaya karşı korumalı şekilde loglanır; SIEM’inize aktarılabilir ve denetimler için hazır KVKK rapor şablonlarıyla gelir.',
      },
      productHref: '/sync/governance',
      productName: 'Monosync Governance',
    },
    {
      requirement: {
        en: 'Data residency & sovereignty',
        tr: 'Veri yerelliği ve egemenliği',
      },
      detail: {
        en: 'Many Turkish organizations prefer or require identity data to stay on infrastructure they control.',
        tr: 'Birçok Türk kuruluşu kimlik verisinin kendi kontrolündeki altyapıda kalmasını tercih eder veya buna mecburdur.',
      },
      capability: {
        en: 'Monofor deploys fully self-hosted, in your datacenter or your cloud tenant in Türkiye, at the same price as SaaS. Your identity data never has to leave your infrastructure.',
        tr: 'Monofor tamamen kendi altyapınızda (Türkiye’deki veri merkezinizde veya bulut aboneliğinizde) SaaS ile aynı fiyata kurulur. Kimlik veriniz altyapınızdan çıkmak zorunda kalmaz.',
      },
      productHref: '/sign',
      productName: 'Monosign',
    },
  ],
  faqs: [
    {
      q: {
        en: 'Does using Monofor make us KVKK compliant?',
        tr: 'Monofor kullanmak bizi KVKK uyumlu yapar mı?',
      },
      a: {
        en: 'No single product makes an organization compliant; KVKK covers legal, organizational, and technical measures together. Monofor implements the identity-related technical measures (authentication, authorization, privileged access, logging) and produces the evidence, which typically covers a substantial part of a technical-measures audit.',
        tr: 'Hiçbir ürün tek başına bir kuruluşu uyumlu yapmaz; KVKK hukuki, idari ve teknik tedbirleri birlikte kapsar. Monofor kimlikle ilgili teknik tedbirleri (kimlik doğrulama, yetkilendirme, ayrıcalıklı erişim, loglama) uygular ve kanıt üretir; bu da teknik tedbir denetiminin önemli bir bölümünü tipik olarak karşılar.',
      },
    },
    {
      q: {
        en: 'Can we run Monofor entirely inside Türkiye?',
        tr: 'Monofor’u tamamen Türkiye içinde çalıştırabilir miyiz?',
      },
      a: {
        en: 'Yes. Self-hosted deployment runs on your own infrastructure (datacenter or local cloud), so identity data, logs, and session recordings stay where you decide. Turkish banks and public institutions use exactly this model.',
        tr: 'Evet. Kendi altyapınızda kurulum, veri merkezinizde veya yerel bulutta çalışır; kimlik verisi, loglar ve oturum kayıtları sizin belirlediğiniz yerde kalır. Türk bankaları ve kamu kurumları tam olarak bu modeli kullanıyor.',
      },
    },
    {
      q: {
        en: 'What evidence can we hand to auditors?',
        tr: 'Denetçilere hangi kanıtları sunabiliriz?',
      },
      a: {
        en: 'Pre-built KVKK report templates, access-review attestations, privileged session recordings with keystroke transcripts, and tamper-evident logs of every authentication and entitlement change, all exportable.',
        tr: 'Hazır KVKK rapor şablonları, erişim gözden geçirme onayları, tuş kaydı dökümlü ayrıcalıklı oturum kayıtları ve her kimlik doğrulama ile yetki değişikliğinin kurcalamaya karşı korumalı logları; tamamı dışa aktarılabilir.',
      },
    },
    {
      q: {
        en: 'How does this relate to BDDK expectations for banks?',
        tr: 'Bu, bankalar için BDDK beklentileriyle nasıl ilişkili?',
      },
      a: {
        en: 'BDDK regulations on bank information systems point in the same direction with stricter teeth: strong authentication, privileged access management, and comprehensive audit trails. The same Monofor controls apply; Türkiye’s oldest bank runs its identity layer on Monofor.',
        tr: 'BDDK’nın banka bilgi sistemleri düzenlemeleri aynı yönü daha sıkı kurallarla işaret eder: güçlü kimlik doğrulama, ayrıcalıklı erişim yönetimi ve kapsamlı denetim izleri. Aynı Monofor kontrolleri burada da geçerlidir; Türkiye’nin en köklü bankası kimlik katmanını Monofor üzerinde çalıştırıyor.',
      },
    },
  ],
}

type Props = { locale: string }

export default function KvkkCompliancePage({ locale }: Props) {
  return (
    <CompliancePage
      content={content}
      locale={locale}
      leadMagnet={
        <AssetDownload
          asset="kvkk-rehber"
          title={{
            en: 'KVKK için kimlik ve erişim rehberi.',
            tr: 'KVKK için kimlik ve erişim rehberi.',
          }}
          copy={{
            en: 'Kişisel veriye erişimi kanıtlanabilir biçimde yönetmek için beş bölümlük kontrol listesi: envanterden değiştirilemez loglara. (Türkçe PDF)',
            tr: 'Kişisel veriye erişimi kanıtlanabilir biçimde yönetmek için beş bölümlük kontrol listesi: erişim envanterinden değiştirilemez loglara.',
          }}
        />
      }
    />
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
